import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, PackagePlus, Pencil, Trash2, FileUp, DownloadCloud } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn } from "../lib/format";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { Empty, Loader, Modal, GlassPage, GlassHeader } from "../components/kit";
import ProductForm from "../components/ProductForm";
import ImportProducts from "../components/ImportProducts";
import { useDebounced } from "../components/ProductSearch";

export default function Products() {
  const { settings } = useSettings();
  const { user } = useAuth() as any;
  const isOwner = (user?.role || "") === "owner";
  const CATEGORIES = settings.categories;
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [list, setList] = useState<any[] | null>(null);
  const [edit, setEdit] = useState<any>(null);
  const [imp, setImp] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const dq = useDebounced(q);

  const load = useCallback(() => {
    api.get("/products", { params: { q: dq, category: cat } }).then((r) => setList(r.data));
  }, [dq, cat]);
  useEffect(() => { load(); }, [load]);

  const loadCatalog = async () => {
    if (!window.confirm("রানা ইলেক্ট্রিকের ডিফল্ট পণ্য তালিকা (৬২০টি) যোগ করবেন? দাম ও স্টক ০ থাকবে, আগে থেকে থাকা পণ্য বাদ যাবে না।")) return;
    setLoadingCatalog(true);
    try {
      const { data } = await api.post("/products/load-catalog");
      toast.success(`${bn(data.added)} টি পণ্য যোগ হয়েছে (মোট ${bn(data.total)})`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoadingCatalog(false);
    }
  };

  const del = async (p: any) => {
    if (!window.confirm(`"${p.name}" মুছে ফেলবেন?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("পণ্য মুছে ফেলা হয়েছে");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <GlassPage testid="products-page">
      <GlassHeader
        title="পণ্য তালিকা"
        subtitle={list ? `মোট ${bn(list.length)} টি পণ্য` : ""}
        actions={<>
          {isOwner && <button onClick={loadCatalog} disabled={loadingCatalog} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 font-semibold transition-all active:scale-95 backdrop-blur disabled:opacity-50" data-testid="products-load-catalog-button"><DownloadCloud size={17} /> {loadingCatalog ? "লোড হচ্ছে..." : "ক্যাটালগ লোড"}</button>}
          <button onClick={() => setImp(true)} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 font-semibold transition-all active:scale-95 backdrop-blur" data-testid="products-import-button"><FileUp size={17} /> এক্সেল ইমপোর্ট</button>
          <Link to="/products/new" className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-semibold transition-all active:scale-95 shadow-lg shadow-emerald-500/25" data-testid="products-add-button"><PackagePlus size={17} /> নতুন পণ্য</Link>
        </>}
      />

      <div className="glass-card p-4 flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="ginp pl-10" placeholder="নাম / কোড / বারকোড / কোম্পানি দিয়ে খুঁজুন" value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search-input" />
        </div>
        <select className="ginp sm:w-56" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="products-category-filter">
          <option value="">সব ক্যাটাগরি</option>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="glass-card overflow-x-auto">
        {!list ? <Loader /> : list.length === 0 ? <Empty text="কোনো পণ্য নেই। নতুন পণ্য যোগ করুন অথবা মেমোতে সরাসরি লিখুন।" /> : (
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="text-slate-400 text-left border-b border-white/10">
                <th className="px-5 py-3 font-medium">পণ্যের নাম</th><th className="px-3 py-3 font-medium">ক্যাটাগরি</th>
                <th className="px-3 py-3 font-medium">কোম্পানি</th><th className="px-3 py-3 font-medium">বারকোড/কোড</th>
                <th className="px-3 py-3 font-medium text-right">দর</th><th className="px-3 py-3 font-medium text-right">স্টক</th><th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`product-row-${p.id}`}>
                  <td className="px-5 py-3 font-semibold text-white">{p.name}</td>
                  <td className="px-3 py-3 text-slate-300">{p.category || "—"}</td>
                  <td className="px-3 py-3 text-slate-300">{p.company || "—"}</td>
                  <td className="px-3 py-3 text-xs text-slate-400">{[p.barcode, p.code].filter(Boolean).join(" / ") || "—"}</td>
                  <td className="px-3 py-3 text-right num font-semibold text-white">{tk(p.rate)}</td>
                  <td className="px-3 py-3 text-right">
                    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full ${p.stock <= p.low_stock ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>{bn(p.stock)} {p.unit}</span>
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-right">
                    <button className="w-8 h-8 inline-grid place-items-center rounded-lg text-slate-300 hover:bg-white/10" onClick={() => setEdit(p)} data-testid={`product-edit-${p.id}`} aria-label="এডিট"><Pencil size={15} /></button>
                    <button className="w-8 h-8 inline-grid place-items-center rounded-lg text-rose-400 hover:bg-rose-500/10" onClick={() => del(p)} data-testid={`product-delete-${p.id}`} aria-label="মুছুন"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title="পণ্য এডিট" wide testid="product-edit-modal">
        {edit && <ProductForm initial={edit} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      </Modal>
      <Modal open={imp} onClose={() => setImp(false)} title="এক্সেল দিয়ে পণ্য ইমপোর্ট" testid="product-import-modal">
        <ImportProducts onDone={() => { load(); }} />
      </Modal>
    </GlassPage>
  );
}
